import React, { useState, useCallback, useMemo, useEffect } from "react";
import { alpha } from "@mui/material/styles";
import { headingText } from "theme/colors";
import {
  Box,
  Grid,
  Typography,
  Stack,
  useMediaQuery,
  useTheme,
  Drawer,
  Alert,
  IconButton,
  Tooltip,
  Avatar,
  Button,
} from "@mui/material";
import { LockOutlined } from "@mui/icons-material";
import FieldsPanel from "./FieldsPanel";
import FieldsPanelMobile from "./FieldsPanelMobile";
import { tourAttr } from "tour";
import DocumentViewer from "./DocumentViewer";
import {
  participantsDropDown,
  getNextAvailableId,
  resizeFieldByKey,
} from "../SignRequestHelper";
import { detectFields } from "../SignRequestService";
import { EMBED_EDITOR_PATH } from "embed/embedMode";
import {
  allocateFieldIds,
  toEditorFieldType,
  withoutOverlappingFields,
} from "../autoPlaceFields";
import {
  openSuccessNotification,
  openErrorNotification,
} from "helpers/methods";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { RootState } from "utils/store";
import {
  selectScreen,
  selectField,
  setSelectedFieldDrawer,
  setSideCollpased,
  setReduxSelectedFile,
  setReduxSelectedParticipant,
  undoFieldChange,
  redoFieldChange,
  deleteFieldOfSelectedFile,
  setClipboardField,
  updateFileInSignRequest,
  updateFieldOfSelectedFile,
  updateFieldInSignRequest,
  setSelectedField as setSelectedFieldRedux,
  updateFileWithFields,
  setHasAutoDetected,
  setPendingAiFields,
  clearPendingAiFields,
} from "redux/appSlice";
import Documents from "./Documents";
import {
  FileField,
  RequestFile,
  RequestParticipant,
} from "../../../models/interfaces";
import ChatWithPdf from "./ChatWithPdf";
import { PresendReviewFocus } from "screens/SignRequest/components/PresendReview/PresendReviewTypes";
import PropertiesDrawer from "./PropertiesDrawer";
import AIInsightsPanel from "./AIInsightsPanel";
import ContractAtAGlanceDialog from "./ContractAtAGlanceDialog";
import DocumentTabMobile from "./DocumentTabMobile";
import { useFeatureState } from "hooks/use-feature";
import FeatureKeys from "global/constants/FeatureKeys";
import FeatureGateBlock from "global/components/FeatureGateBlock/FeatureGateBlock";
import strings from "global/constants/StringConstants";
import UndoRedoButtons from "./UndoRedoButtons";
import AssignExistingFieldsModal from "./AssignExistingFieldsModal";
import { useLocation } from "react-router-dom";
import viewpaths from "global/constants/ViewPathConstants";
import { AppModal, bodyTextSx } from "global/components/AppModal";
import editorTokens from "../SignRequestEditor.tokens";
import {
  AssistantTabIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  CloseIcon as EditorCloseIcon,
  DocumentsTabIcon,
  FitScreenIcon,
  InsightsTabIcon,
  PanelToggleIcon,
  PlusIcon,
  PropertiesTabIcon,
  SparkleIcon,
} from "./EditorIcons";
import RightPanelHeader from "./RightPanelHeader";

interface SelfSignerInfoProps {
  userName?: string;
  email?: string;
  isMobile?: boolean;
}

const SelfSignerInfo = ({ userName, email, isMobile }: SelfSignerInfoProps) => {
  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={1.5}
      sx={{ mb: isMobile ? 1 : 3 }}
    >
      <Avatar
        sx={{
          fontWeight: 600,
          fontSize: "0.75rem",
          width: 32,
          height: 32,
          mr: isMobile ? 1 : 1.5,
        }}
      >
        {userName
          ?.split(" ")
          .filter(Boolean)
          .slice(0, 2)
          .map((word) => word[0])
          .join("")
          .toUpperCase()}
      </Avatar>

      <Stack>
        <Typography
          variant="body2"
          sx={{
            fontWeight: 500,
          }}
        >
          {userName}
        </Typography>
        {!isMobile && (
          <Typography
            variant="subtitle2"
            sx={{
              fontSize: "0.75rem",
              fontWeight: 400,
            }}
          >
            {email}
          </Typography>
        )}
      </Stack>
    </Stack>
  );
};

const useResponsiveGridSizes = (isWide: any, isMobile: any) => {
  return useMemo(() => {
    if (isMobile) {
      return {
        leftPanel: { xs: 12, sm: 12, md: 1.5, lg: 1.5, xl: 1.5 },
        mainPanel: { xs: 12, sm: 12, md: 7.5, lg: 8, xl: 8 },
        rightPanel: { xs: 12, sm: 3, md: 3, lg: 2.5, xl: 2.5 },
      };
    }
    return {
      leftPanel: { xs: 3, sm: 3, md: 1.5, lg: 1.5, xl: 1.5 },
      mainPanel: { xs: 6, sm: 6, md: 7.5, lg: 8, xl: 8 },
      rightPanel: { xs: 3, sm: 3, md: 3, lg: 2.5, xl: 2.5 },
    };
  }, [isWide, isMobile]);
};

const PropertiesPanel = React.memo(() => (
  <Box>
    <Typography
      variant="h6"
      sx={(theme) => ({
        p: 1.5,
        mb: 2,
        fontWeight: 500,
        backgroundColor: alpha(theme.palette.primary.main, 0.09),
      })}
    >
      Properties
    </Typography>
    {/* <FieldProperties /> */}
  </Box>
));

PropertiesPanel.displayName = "PropertiesPanel";

const rightPanelIcons = [
  {
    id: "documents" as const,
    icon: DocumentsTabIcon,
    title: "Documents",
    label: "Documents",
  },
  {
    id: "properties" as const,
    icon: PropertiesTabIcon,
    label: "Field properties",
    title: "Field properties",
  },

  {
    id: "chat" as const,
    icon: AssistantTabIcon,
    label: "AI Assistant",
    title: "AI Assistant",
  },
  {
    id: "insights" as const,
    icon: InsightsTabIcon,
    label: "AI Insights",
    title: "AI Insights",
  },
];
const GatedPanel = ({
  icon,
  title,
  headerAction,
  aiAssistantState,
}: {
  icon: React.ReactNode;
  title: string;
  headerAction?: React.ReactNode;
  aiAssistantState: any;
}) => (
  <Stack sx={{ height: "100%", minHeight: 0 }}>
    <RightPanelHeader icon={icon} title={title} headerAction={headerAction} />
    <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
      <FeatureGateBlock
        variant="inline"
        status={
          aiAssistantState.status === strings.FEATURE_STATUS_ADMIN_DISABLED
            ? strings.FEATURE_STATUS_ADMIN_DISABLED
            : strings.FEATURE_STATUS_NOT_IN_PLAN
        }
        disabledMessage={aiAssistantState.disabledMessage}
      />
    </Box>
  </Stack>
);
const RIGHT_RAIL_WIDTH = editorTokens.tabRail.width;

/** Opening AI-Assistant prompts from `Sign Request Editor.dc.html`. */
const EDITOR_AI_PROMPTS = [
  "Summarize this document before I send it",
  "Is any standard clause missing?",
  "What are the key terms and conditions in this document?",
  "Are there any unusual or risky clauses in this document?",
];

/** Ghost square used by every button in the canvas toolbar. */
const canvasIconButtonSx = (
  size: number,
  radius: string,
  color: string = editorTokens.canvas.toggleColor,
) => ({
  width: size,
  height: size,
  flexShrink: 0,
  borderRadius: radius,
  color,
  "&:hover": { backgroundColor: editorTokens.palette.surface },
  "&.Mui-disabled": { color: editorTokens.palette.borderMain },
});

/** Round page-stepper button inside the floating page pill. */
const pageNavButtonSx = {
  width: editorTokens.pageNav.buttonSize,
  height: editorTokens.pageNav.buttonSize,
  borderRadius: "50%",
  color: editorTokens.pageNav.activeColor,
  "&:hover": { backgroundColor: editorTokens.palette.surface },
  "&.Mui-disabled": { color: editorTokens.pageNav.idleColor },
};

/** PLACING FIELDS FOR — the rail's uppercase section label. */
export const railEyebrowSx = {
  fontSize: editorTokens.leftRail.eyebrowFontSize,
  fontWeight: editorTokens.leftRail.eyebrowFontWeight,
  letterSpacing: editorTokens.leftRail.eyebrowLetterSpacing,
  textTransform: "uppercase" as const,
  lineHeight: "normal",
  color: editorTokens.leftRail.eyebrowColor,
  mb: editorTokens.leftRail.eyebrowMarginBottom,
};

const SELF_SIGN_INTERACTIVE_TYPES = [strings.signature, strings.initials];

/** "Show on page N": when the second jump runs, after the first may have found the page not yet mounted. */
const REVIEW_FOCUS_SETTLE_MS = 400;

const SignRequestEditor = ({
  setLoader,
  onManageParticipants,
  onAddDocuments,
  rolesRail,
  reviewFocus,
  onReviewFocusHandled,
}: {
  setLoader: Function;
  /** "+ Manage" in the left rail returns to the Approvers and Signers step. */
  onManageParticipants?: () => void;
  /** "+ Add" in the Documents panel returns to the Upload Document step. */
  onAddDocuments?: () => void;
  /**
   * Replaces the "Placing fields for" block at the top of the left rail, for a
   * flow whose recipients are named *roles* defined here rather than people
   * chosen on an earlier step (bulk send's upload path). The field palette
   * below it, and everything else in the editor, are unchanged — a caller that
   * omits this gets today's participant rail exactly as before.
   */
  rolesRail?: React.ReactNode;
  /**
   * "Show on page N" from the pre-send review (which opens over this editor): the document to open, the
   * page to go to and the field to select once that document has loaded.
   */
  reviewFocus?: PresendReviewFocus | null;
  onReviewFocusHandled?: () => void;
}) => {
  const location = useLocation();
  const {
    signRequest,
    selectedFile,
    selectedParticipant,
    isBulkSend,
    selectedFieldDrawer,
    sideCollpased,
    selectedField: reduxSelectedField,
    clipboardField,
    assignExistingFields,
    isRequest,
  } = useAppSelector((state: RootState) => state.app);

  const isSelfSign = location.pathname === viewpaths.createSelfSignRequestPath;
  const isCreateFlow =
    location.pathname === viewpaths.createSignRequestsViewPath ||
    location.pathname === viewpaths.createSelfSignRequestPath ||
    location.pathname === viewpaths.createTemplateViewPath ||
    // Bulk send lays a document out the same way, so it gets the same
    // field auto-detection rather than silently going without it.
    location.pathname === viewpaths.bulkSendViewPath ||
    // The embedded wizard too: its detection call goes to the draft's embed endpoint.
    location.pathname === EMBED_EDITOR_PATH;

  const [selectedPreviewField, setSelectedPreviewField] =
    useState<FileField | null>();
  const [docLoading, setDocLoading] = useState(true);
  const [selectedField, setSelectedField] = useState<string | null>("");
  const [isRightDrawerOpen, setIsRightDrawerOpen] = useState(false);
  const [error, setError] = useState("");
  const [activeRightPanel, setActiveRightPanel] = useState<
    "chat" | "insights" | "documents" | "properties" | null
  >("documents");
  /**
   * The design opens on the AI Assistant tab. Only honour that once we know the
   * feature is actually available — otherwise the editor would open on a
   * feature-gate block.
   */
  const hasAppliedDefaultPanel = React.useRef(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [isConfirmAutoPlaceOpen, setIsConfirmAutoPlaceOpen] = useState(false);
  const prevSelectedFieldDrawer = React.useRef(false);
  const prevSelectedFieldId = React.useRef<number | undefined>(undefined);
  const hasInitializedFile = React.useRef(false);
  const selectedFileIdRef = React.useRef<string | null>(null);
  const theme = useTheme();
  const isMobile = useAppSelector(selectScreen);
  const isTablet = useMediaQuery(theme.breakpoints.down("lg"));
  const isNarrowViewport = useMediaQuery(theme.breakpoints.down("md"));
  const isWide = useMediaQuery("(min-width:1167px)");
  const participants = (signRequest.signers || []).filter(
    (signer: RequestParticipant) => !signer.cc,
  );
  const [isFieldWithinBounds, setIsFieldWithinBounds] = useState(false);
  const [previewCoords, setPreviewCoords] = useState({
    x: 0,
    y: 0,
  });
  const gridSizes = useResponsiveGridSizes(isWide, isMobile);

  const [isLeftPanelOpen, setIsLeftPanelOpen] = useState(true);
  const [isRightPanelOpen, setIsRightPanelOpen] = useState(true);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [pageInfo, setPageInfo] = useState({ current: 1, total: 0 });
  const goToPageRef = React.useRef<((page: number) => void) | null>(null);
  const handlePageInfoChange = useCallback(
    (info: { current: number; total: number }) =>
      setPageInfo((prev) =>
        prev.current === info.current && prev.total === info.total
          ? prev
          : info,
      ),
    [],
  );

  const isLeftPanelVisible = isMobile || isLeftPanelOpen;

  const mainPanelSizes = useMemo(() => {
    if (!isMobile && !isRightPanelOpen) {
      return { xs: true, sm: true, md: true, lg: true, xl: true };
    }
    if (isLeftPanelVisible) return gridSizes.mainPanel;
    return {
      xs: gridSizes.mainPanel.xs + gridSizes.leftPanel.xs,
      sm: gridSizes.mainPanel.sm + gridSizes.leftPanel.sm,
      md: gridSizes.mainPanel.md + gridSizes.leftPanel.md,
      lg: gridSizes.mainPanel.lg + gridSizes.leftPanel.lg,
      xl: gridSizes.mainPanel.xl + gridSizes.leftPanel.xl,
    };
  }, [gridSizes, isLeftPanelVisible, isRightPanelOpen, isMobile]);

  const selectedFileName = useMemo(() => {
    const docPath =
      selectedFile?.unsignedDoc || selectedFile?.unsignedPdf || "";
    return docPath.split("/").pop() || "";
  }, [selectedFile?.unsignedDoc, selectedFile?.unsignedPdf]);

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [bottomBarHeight, setBottomBarHeight] = useState(112);
  const [pasteAnchor, setPasteAnchor] = useState<{
    page: number;
    fieldPageX: number;
    fieldPageY: number;
  } | null>(null);
  const [isDetectingFields, setIsDetectingFields] = useState(false);
  const [isContractDialogOpen, setIsContractDialogOpen] = useState(false);
  const dispatch = useAppDispatch();
  const loggedInUser = useAppSelector((state: RootState) => state.auth);
  const { hasAutoDetected = {}, pendingAiFields = {} } = useAppSelector(
    (state: RootState) => state.app || {},
  );

  useEffect(() => {
    if (!isCreateFlow) return;
    const envelopeId = (signRequest as any).id;
    const unsignedPdf = selectedFile?.unsignedDoc;

    if (
      !envelopeId ||
      !unsignedPdf ||
      hasAutoDetected[unsignedPdf] ||
      pendingAiFields[unsignedPdf] ||
      isDetectingFields
    ) {
      return;
    }

    const autoDetect = async () => {
      dispatch(setHasAutoDetected({ docId: unsignedPdf, detected: true }));
      setIsDetectingFields(true);
      try {
        // `unsignedPdf` here is the display name that keys the guard state; the
        // server only knows the stored name, which differs for a converted upload.
        const response = await detectFields(
          envelopeId,
          selectedFile?.unsignedPdf || unsignedPdf,
          isRequest,
        );
        const detected: FileField[] = Array.isArray(response) ? response : [];
        if (detected.length > 0) {
          dispatch(
            setPendingAiFields({ docId: unsignedPdf, fields: detected }),
          );
        }
      } catch (error: any) {
        // Field detection runs in the background — never surface its failure to
        // the user. The guard stays set so a persistent error doesn't retry-loop.
      } finally {
        setIsDetectingFields(false);
      }
    };
    autoDetect();
  }, [
    isCreateFlow,
    selectedFile?.unsignedDoc,
    selectedFile?.unsignedPdf,
    signRequest,
    hasAutoDetected,
    pendingAiFields,
    isDetectingFields,
    isRequest,
  ]);

  const handleAutoPlaceFields = useCallback(
    async (skipConfirm: boolean | React.MouseEvent = false) => {
      const unsignedPdf = selectedFile?.unsignedDoc;
      if (!unsignedPdf) {
        return;
      }
      const detected = pendingAiFields[unsignedPdf];
      if (!detected || detected.length === 0) {
        return;
      }
      const nonCcSigners = (signRequest.signers || []).filter(
        (s: RequestParticipant) => !s.cc,
      );

      if (nonCcSigners.length === 0) {
        openErrorNotification(
          "Add at least one signer before detecting fields.",
        );
        return;
      }
      if (
        selectedFile?.fields?.length > 0 &&
        // !window.confirm(
        //   "Existing fields will remain. Detected fields will be added on top. Continue?",
        // )
        skipConfirm !== true
      ) {
        setIsConfirmAutoPlaceOpen(true);
        return;
      }

      const defaultSigner = nonCcSigners[0];
      // Safety net: never place a field on top of one the document already has
      // (its own form fields, or fields an earlier Auto-place put there).
      const toPlace = withoutOverlappingFields(
        detected,
        selectedFile?.fields || [],
        (selectedFile as any)?.dimensions,
      );
      if (toPlace.length === 0) {
        dispatch(clearPendingAiFields({ docId: unsignedPdf }));
        openSuccessNotification(
          "No new fields found. The document's fields are already placed.",
        );
        return;
      }
      const newIds = allocateFieldIds(selectedFile?.fields || [], toPlace.length);
      const typeCount: Record<string, number> = {};

      if (selectedFile?.fields) {
        selectedFile.fields.forEach((f: FileField) => {
          if (f.signerIndex === defaultSigner.id) {
            const t = f.type ?? "text";
            typeCount[t] = (typeCount[t] || 0) + 1;
          }
        });
      }

      const mapped: FileField[] = toPlace.map((f: any, index: number) => {
        const type = toEditorFieldType(f.type);
        typeCount[type] = (typeCount[type] ?? 0) + 1;
        const labelSuffix = typeCount[type];
        const id = newIds[index];

        const isRadio = type === strings.radioButton || type === "radio";
        const generatedLabel = isRadio
          ? `Option 1`
          : (() => {
              const baseName =
                defaultSigner.name || defaultSigner.requestRole || "Signer";
              const firstName = baseName.includes(" ")
                ? baseName.split(" ")[0]
                : baseName;
              return `${firstName}-${type?.split(" ").join("_").toLowerCase()}-${labelSuffix}`;
            })();
        return {
          ...f,
          type,
          id,
          name: `${type}-${id}`,
          signerIndex: defaultSigner.id,
          color: defaultSigner.color,
          x: f.x ?? 0,
          y: f.y ?? 0,
          w: f.w ?? 0,
          h: f.h ?? 0,
          top: 0,
          left: 0,
          page: f.page ?? 0,
          maxLength: f.maxLength || 200,
          required: f.required ?? false,
          hidden: f.hidden ?? false,
          readOnly: f.readOnly ?? false,
          tabLabel: f.tabLabel || "",
          label: f.label || generatedLabel,
          toolTip: f.toolTip ?? "",
          validationPattern: f.validationPattern ?? "none",
          validationMessage: f.validationMessage ?? "",
          placeholder: f.placeholder ?? "",
          multiLine: f.multiLine ?? false,
          styles: f.styles ?? {
            fontFamily: "Times",
            color: headingText,
            size: 16,
            fontWeight: { bold: false, italic: false, underline: false },
          },
          existingField: false,
          isAutoPlaced: true,
        } as FileField;
      });

      const updatedFile = {
        ...selectedFile,
        fields: [...(selectedFile.fields || []), ...mapped],
      };
      dispatch(setReduxSelectedFile(updatedFile));
      dispatch(updateFileWithFields(updatedFile));

      dispatch(clearPendingAiFields({ docId: unsignedPdf }));
      openSuccessNotification(
        `${mapped.length} field${mapped.length === 1 ? "" : "s"} placed. Reassign signers as needed.`,
      );
    },
    [signRequest, selectedFile, dispatch, pendingAiFields],
  );

  const aiAssistantState = useFeatureState(FeatureKeys.AI_ASSISTANT);
  // The embedded editor (partner iframe) offers the same AI tabs: its calls go to the
  // draft's embed endpoints (askPdf / fetchDocumentSummary branch on embed mode).
  const visibleRightPanelIcons = rightPanelIcons;
  const isAIAvailable =
    aiAssistantState.status === strings.FEATURE_STATUS_AVAILABLE;
  const aiDisabledTooltip =
    aiAssistantState.status === strings.FEATURE_STATUS_ADMIN_DISABLED
      ? ((aiAssistantState as any).disabledMessage ??
        strings.FEATURE_GATE_TEMPORARILY_UNAVAILABLE_TOOLTIP)
      : aiAssistantState.status === strings.FEATURE_STATUS_NOT_IN_PLAN
        ? strings.FEATURE_GATE_UPSELL_TOOLTIP
        : "";

  useEffect(() => {
    if (isMobile || hasAppliedDefaultPanel.current) return;
    if (isAIAvailable) {
      hasAppliedDefaultPanel.current = true;
      setActiveRightPanel("chat");
    }
  }, [isAIAvailable, isMobile]);

  const pasteFieldAt = React.useCallback(
    (page: number, fieldPageX: number, fieldPageY: number) => {
      if (!clipboardField || !selectedFile) return;
      const cloned: any = JSON.parse(JSON.stringify(clipboardField));
      const w = cloned.fieldPageWidth || 0;
      const h = cloned.fieldPageHeight || 0;
      let x = fieldPageX - w / 2;
      let y = fieldPageY - h / 2;
      const maxX = Math.max(0, 100 - w);
      const maxY = Math.max(0, 100 - h);
      x = Math.min(Math.max(x, 0), maxX);
      y = Math.min(Math.max(y, 0), maxY);
      const newField: any = {
        ...cloned,
        id: getNextAvailableId(selectedFile),
        page,
        fieldPageX: x,
        fieldPageY: y,
        existingField: false,
      };
      const updatedFile: any = {
        ...selectedFile,
        fields: [...selectedFile.fields, newField],
      };
      dispatch(setReduxSelectedFile(updatedFile));
      dispatch(updateFileInSignRequest(updatedFile));
    },
    [clipboardField, selectedFile, dispatch],
  );

  // Arrow-key nudging for the selected field (WCAG 2.1.1): the only other way
  // to move a placed field is a mouse drag. Arrow keys move by 1% of the page,
  // Shift+Arrow by 5%, clamped inside the page. Alt+Arrow resizes the same way
  // (Right/Down grow, Left/Up shrink), since a resize handle is mouse-only too.
  // Ignored while typing in an input so field labels/values stay editable.
  useEffect(() => {
    const isEditableTarget = (target: EventTarget | null): boolean => {
      if (!target || !(target instanceof HTMLElement)) return false;
      const tag = target.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT")
        return true;
      if (target.isContentEditable) return true;
      return false;
    };

    const handleNudge = (e: KeyboardEvent) => {
      if (e.ctrlKey || e.metaKey) return;
      const arrowKeys = ["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight"];
      if (!arrowKeys.includes(e.key)) return;
      if (isEditableTarget(e.target)) return;

      const f: any = reduxSelectedField;
      if (!f?.id || !f?.type) return;
      e.preventDefault();

      const step = e.shiftKey ? 5 : 1;

      if (e.altKey) {
        const { fieldPageWidth: fw, fieldPageHeight: fh } = resizeFieldByKey(
          f,
          e.key,
          step,
          f.type === strings.checkbox,
        );
        // Keep the PDF-point size in sync with the percentages, the same way
        // DocumentViewer's onFieldResize does, or the field snaps back.
        const dims = (selectedFile as any)?.dimensions?.[f.page];
        const resize = {
          id: f.id,
          existingField: false,
          fieldPageWidth: fw,
          fieldPageHeight: fh,
          ...(dims?.x && dims?.y
            ? { w: (fw / 100) * dims.x, h: (fh / 100) * dims.y }
            : {}),
        };
        dispatch(updateFieldOfSelectedFile(resize));
        dispatch(updateFieldInSignRequest(resize));
        dispatch(setSelectedFieldRedux({ ...f, ...resize }));
        return;
      }

      let x = typeof f.fieldPageX === "number" ? f.fieldPageX : 0;
      let y = typeof f.fieldPageY === "number" ? f.fieldPageY : 0;
      if (e.key === "ArrowUp") y -= step;
      else if (e.key === "ArrowDown") y += step;
      else if (e.key === "ArrowLeft") x -= step;
      else if (e.key === "ArrowRight") x += step;

      const w = typeof f.fieldPageWidth === "number" ? f.fieldPageWidth : 0;
      const h = typeof f.fieldPageHeight === "number" ? f.fieldPageHeight : 0;
      x = Math.min(Math.max(x, 0), Math.max(0, 100 - w));
      y = Math.min(Math.max(y, 0), Math.max(0, 100 - h));

      const changes = { id: f.id, x, y, fieldPageX: x, fieldPageY: y };
      dispatch(updateFieldOfSelectedFile(changes));
      dispatch(updateFieldInSignRequest(changes));
      dispatch(setSelectedFieldRedux({ ...f, ...changes }));
    };

    window.addEventListener("keydown", handleNudge);
    return () => window.removeEventListener("keydown", handleNudge);
  }, [dispatch, reduxSelectedField, selectedFile]);

  useEffect(() => {
    selectedFileIdRef.current =
      selectedFile?.unsignedDoc || selectedFile?.unsignedPdf || null;
  }, [selectedFile]);

  useEffect(() => {
    if (signRequest.files && signRequest.files.length > 0) {
      const currentSelectedFileId = selectedFileIdRef.current;

      const currentFile = currentSelectedFileId
        ? signRequest.files.find((file: any) => {
            const fileId = file.unsignedDoc || file.unsignedPdf;
            return fileId === currentSelectedFileId;
          })
        : null;

      if (!currentFile) {
        const first = signRequest.files[0] as any;
        const normalizedFirst: any = {
          ...first,
          unsignedDoc: first.unsignedDoc || first.unsignedPdf || "",
          unsignedDocUrl: first.unsignedDocUrl || first.unsignedPdfUrl || "",
        };
        dispatch(setReduxSelectedFile(normalizedFirst));
        selectedFileIdRef.current = normalizedFirst.unsignedDoc;
        hasInitializedFile.current = true;
      } else if (!hasInitializedFile.current) {
        if (!currentSelectedFileId) {
          const first = signRequest.files[0] as any;
          const normalizedFirst: any = {
            ...first,
            unsignedDoc: first.unsignedDoc || first.unsignedPdf || "",
            unsignedDocUrl: first.unsignedDocUrl || first.unsignedPdfUrl || "",
          };
          dispatch(setReduxSelectedFile(normalizedFirst));
          selectedFileIdRef.current = normalizedFirst.unsignedDoc;
        }
        hasInitializedFile.current = true;
      }
    }
    // A CC recipient signs nothing, so it is never a valid target for field
    // placement — and it isn't in the rail's own `participants` list either.
    // Picking one (which a plain `signers[0]` does whenever the first
    // recipient receives a copy) sent every placed field to a signer the
    // editor never shows, leaving the real signers with no signature field
    // and the step failing on "add at least one signature field for each
    // signer".
    const signableParticipants = (signRequest.signers || []).filter(
      (s: RequestParticipant) => !s.cc,
    );

    // Re-select not only when nothing is chosen, but whenever the chosen
    // participant is no longer one fields can be placed for. The whole `app`
    // slice is persisted to localStorage, so a selection saved while pointing
    // at a recipient who has since become a CC comes back on the next load
    // with a name and email — non-blank, and so invisible to the emptiness
    // check alone. The rail only ever offers signable participants, so a
    // selection the user made by hand always satisfies this and is left alone.
    // With no signable recipient at all there is nothing better to switch to,
    // so leave the selection alone rather than re-dispatching the same
    // fallback every time the request changes.
    const isSelectionPlaceable =
      !signableParticipants.length ||
      (selectedParticipant &&
        signableParticipants.some(
          (s: RequestParticipant) => s.id === selectedParticipant.id,
        ));

    if (
      signRequest.signers &&
      signRequest.signers.length > 0 &&
      (!selectedParticipant ||
        (!selectedParticipant.name &&
          !selectedParticipant.requestRole &&
          !selectedParticipant.email) ||
        !isSelectionPlaceable)
    ) {
      const searchParams = new URLSearchParams(window.location.search);
      const urlSignerId = searchParams.get("signerId");

      // Fall back to `signers[0]` only when every recipient is a CC, so the
      // selection is never left empty.
      let participantToSelect =
        signableParticipants[0] ?? signRequest.signers[0];

      if (urlSignerId) {
        const idToMatch = Number(urlSignerId);

        let matchingSigner;
        if (idToMatch > 0) {
          // A deep link naming a CC recipient is honoured no further than the
          // list above allows — same reason: fields can't be placed for one.
          matchingSigner = signableParticipants.find(
            (s: RequestParticipant) => s.id === idToMatch,
          );
        } else {
          matchingSigner = participantToSelect;
        }
        if (matchingSigner) {
          participantToSelect = matchingSigner;
        }
      }
      dispatch(setReduxSelectedParticipant(participantToSelect));
    }
  }, [signRequest, dispatch]);

  useEffect(() => {
    const hasUnassignedFields = signRequest.files.some((file: RequestFile) => {
      const docId = file.unsignedDoc || file.unsignedPdf || "";
      return (
        assignExistingFields?.[docId] ||
        file.fields?.some((f: FileField) => f.existingField)
      );
    });

    if (hasUnassignedFields) {
      setShowAssignModal(true);
    } else {
      setShowAssignModal(false);
    }
  }, [signRequest.files, assignExistingFields]);

  useEffect(() => {
    const isEditableTarget = (target: EventTarget | null): boolean => {
      if (!target || !(target instanceof HTMLElement)) return false;
      const tag = target.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT")
        return true;
      if (target.isContentEditable) return true;
      return false;
    };

    const isInsidePlacedField = (target: EventTarget | null): boolean => {
      if (!target || !(target instanceof HTMLElement)) return false;
      return target.closest(".react-draggable") !== null;
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      const docId =
        selectedFile?.unsignedDoc || selectedFile?.unsignedPdf || "";
      const editable = isEditableTarget(e.target);
      const insidePlacedField = isInsidePlacedField(e.target);

      if (e.ctrlKey || e.metaKey) {
        if (editable) return;
        const key = e.key.toLowerCase();
        const isUndo = key === "z" && !e.shiftKey;
        const isRedo = (key === "z" && e.shiftKey) || key === "y";
        const isCopy = key === "c";
        const isCut = key === "x";
        const isPaste = key === "v";

        if (isCopy) {
          if (!reduxSelectedField?.id || !reduxSelectedField?.type) {
            return;
          }
          e.preventDefault();
          dispatch(setClipboardField(reduxSelectedField));
          return;
        }

        if (isCut) {
          if (!reduxSelectedField?.id || !reduxSelectedField?.type) {
            return;
          }
          e.preventDefault();
          dispatch(setClipboardField(reduxSelectedField));
          dispatch(deleteFieldOfSelectedFile(reduxSelectedField));
          return;
        }

        if (isPaste) {
          if (!clipboardField || !pasteAnchor) return;
          e.preventDefault();
          pasteFieldAt(
            pasteAnchor.page,
            pasteAnchor.fieldPageX,
            pasteAnchor.fieldPageY,
          );
          return;
        }

        if (!isUndo && !isRedo) return;
        if (!docId) return;

        e.preventDefault();
        if (isUndo) {
          dispatch(undoFieldChange({ docId }));
        } else {
          dispatch(redoFieldChange({ docId }));
        }
        return;
      }

      if (e.key === "Delete" || e.key === "Backspace") {
        // Allow field deletion when focus is on the body OR inside the field
        // itself (its inner textarea). Block when focus is in an external
        // input (property drawer, etc.) so typing there is preserved.
        if (editable && !insidePlacedField) return;

        // Backspace inside the field's textarea is reserved for character
        // editing — only Delete should remove a focused-on-field field.
        if (e.key === "Backspace" && editable && insidePlacedField) return;

        const fieldId = reduxSelectedField?.id;
        if (!fieldId || !reduxSelectedField?.type) return;
        e.preventDefault();
        dispatch(deleteFieldOfSelectedFile(reduxSelectedField));
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [
    dispatch,
    selectedFile?.unsignedDoc,
    selectedFile?.unsignedPdf,
    reduxSelectedField,
    clipboardField,
    pasteAnchor,
    pasteFieldAt,
  ]);

  useEffect(() => {
    const isEditableTarget = (target: EventTarget | null): boolean => {
      if (!target || !(target instanceof HTMLElement)) return false;
      const tag = target.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT")
        return true;
      if (target.isContentEditable) return true;
      return false;
    };

    const isInsidePlacedField = (target: EventTarget | null): boolean => {
      if (!target || !(target instanceof HTMLElement)) return false;
      return target.closest(".react-draggable") !== null;
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      const docId =
        selectedFile?.unsignedDoc || selectedFile?.unsignedPdf || "";
      const editable = isEditableTarget(e.target);
      const insidePlacedField = isInsidePlacedField(e.target);

      if (e.ctrlKey || e.metaKey) {
        if (editable) return;
        const key = e.key.toLowerCase();
        const isUndo = key === "z" && !e.shiftKey;
        const isRedo = (key === "z" && e.shiftKey) || key === "y";
        const isCopy = key === "c";
        const isCut = key === "x";
        const isPaste = key === "v";

        if (isCopy) {
          if (!reduxSelectedField?.id || !reduxSelectedField?.type) {
            return;
          }
          e.preventDefault();
          dispatch(setClipboardField(reduxSelectedField));
          return;
        }

        if (isCut) {
          if (!reduxSelectedField?.id || !reduxSelectedField?.type) {
            return;
          }
          e.preventDefault();
          dispatch(setClipboardField(reduxSelectedField));
          dispatch(deleteFieldOfSelectedFile(reduxSelectedField));
          return;
        }

        if (isPaste) {
          if (!clipboardField || !pasteAnchor) return;
          e.preventDefault();
          pasteFieldAt(
            pasteAnchor.page,
            pasteAnchor.fieldPageX,
            pasteAnchor.fieldPageY,
          );
          return;
        }

        if (!isUndo && !isRedo) return;
        if (!docId) return;

        e.preventDefault();
        if (isUndo) {
          dispatch(undoFieldChange({ docId }));
        } else {
          dispatch(redoFieldChange({ docId }));
        }
        return;
      }

      if (e.key === "Delete" || e.key === "Backspace") {
        // Allow field deletion when focus is on the body OR inside the field
        // itself (its inner textarea). Block when focus is in an external
        // input (property drawer, etc.) so typing there is preserved.
        if (editable && !insidePlacedField) return;

        // Backspace inside the field's textarea is reserved for character
        // editing — only Delete should remove a focused-on-field field.
        if (e.key === "Backspace" && editable && insidePlacedField) return;

        const fieldId = reduxSelectedField?.id;
        if (!fieldId || !reduxSelectedField?.type) return;
        e.preventDefault();
        dispatch(deleteFieldOfSelectedFile(reduxSelectedField));
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [
    dispatch,
    selectedFile?.unsignedDoc,
    selectedFile?.unsignedPdf,
    reduxSelectedField,
    clipboardField,
    pasteAnchor,
    pasteFieldAt,
  ]);

  useEffect(() => {
    const justOpened = selectedFieldDrawer && !prevSelectedFieldDrawer.current;
    const fieldChanged =
      selectedFieldDrawer &&
      reduxSelectedField &&
      reduxSelectedField.id !== prevSelectedFieldId.current;

    if ((justOpened || fieldChanged) && reduxSelectedField) {
      const hasValidId =
        reduxSelectedField.id !== undefined &&
        reduxSelectedField.id !== null &&
        reduxSelectedField.id !== 0;

      const hasType = reduxSelectedField.type && reduxSelectedField.type !== "";

      if (hasValidId && hasType && !isMobile) {
        setActiveRightPanel("properties");
      }
    }

    prevSelectedFieldDrawer.current = selectedFieldDrawer;
    if (reduxSelectedField?.id !== undefined) {
      prevSelectedFieldId.current = reduxSelectedField.id;
    }
  }, [selectedFieldDrawer, reduxSelectedField]);

  // "Show on page N" from the pre-send review: open the finding's document, wait for its pages, then
  // go to the page and select the field the finding is about (the selection highlights it).
  const reviewFocusSwitchingRef = React.useRef(false);
  useEffect(() => {
    if (!reviewFocus) return;
    const isTarget = (file?: RequestFile | null) =>
      Boolean(file) &&
      ((file!.unsignedPdf || file!.unsignedDoc) === reviewFocus.fileName ||
        file!.unsignedDoc === reviewFocus.fileName);
    if (!isTarget(selectedFile)) {
      const target = (signRequest.files || []).find((file: RequestFile) =>
        isTarget(file),
      ) as any;
      if (!target) {
        onReviewFocusHandled?.();
        return;
      }
      reviewFocusSwitchingRef.current = true;
      dispatch(
        setReduxSelectedFile({
          ...target,
          unsignedDoc: target.unsignedDoc || target.unsignedPdf || "",
          unsignedDocUrl: target.unsignedDocUrl || target.unsignedPdfUrl || "",
        }),
      );
      return;
    }
    if (reviewFocusSwitchingRef.current) {
      // The viewer still reports the previous document until it drops it; the next load is this one.
      if (docLoading || pageInfo.total === 0) {
        reviewFocusSwitchingRef.current = false;
      }
      return;
    }
    if (docLoading || pageInfo.total === 0) return;
    const page = Math.min(reviewFocus.page, pageInfo.total);
    goToPageRef.current?.(page);
    // Pages mount a few at a time: a page the first jump found unmounted is there on the second. Not
    // cleared on re-run -- handling the focus clears it, which re-runs this effect at once; after an
    // unmount the ref is null and the call does nothing.
    window.setTimeout(
      () => goToPageRef.current?.(page),
      REVIEW_FOCUS_SETTLE_MS,
    );
    const field = reviewFocus.fieldId
      ? (selectedFile?.fields || []).find(
          (f: FileField) => String(f.id) === reviewFocus.fieldId,
        )
      : undefined;
    if (field) {
      // As a click on the field does: select it and show its properties.
      if (sideCollpased) dispatch(setSideCollpased(false));
      dispatch(selectField(field));
      dispatch(setSelectedFieldDrawer(true));
    }
    onReviewFocusHandled?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reviewFocus, selectedFile, docLoading, pageInfo.total]);

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleDrawerToggle = useCallback(() => {
    setIsRightDrawerOpen((prev) => !prev);
  }, []);

  const handleDrawerClose = useCallback(() => {
    setIsRightDrawerOpen(false);
  }, []);

  const handleRightPanelChange = useCallback(
    (panel: "chat" | "insights" | "documents" | "properties") => {
      // Any explicit pick wins over the deferred AI-Assistant default.
      hasAppliedDefaultPanel.current = true;
      setActiveRightPanel(panel);
      setIsRightPanelOpen(true);
    },
    [],
  );

  const isIOS = useMemo(() => {
    if (typeof window === "undefined") return false;
    return (
      /iPad|iPhone|iPod/.test(navigator.userAgent) ||
      (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1)
    );
  }, []);

  useEffect(() => {
    if (!isIOS) return;

    const setRealVh = () => {
      const vh = window.innerHeight * 0.01;
      document.documentElement.style.setProperty("--real-vh", `${vh}px`);
    };

    setRealVh();

    // visualViewport fires more reliably on iOS than window resize
    // because it tracks the visual viewport (excludes browser chrome).
    if (window.visualViewport) {
      window.visualViewport.addEventListener("resize", setRealVh);
      window.visualViewport.addEventListener("scroll", setRealVh);
    } else {
      window.addEventListener("resize", setRealVh);
    }

    return () => {
      if (window.visualViewport) {
        window.visualViewport.removeEventListener("resize", setRealVh);
        window.visualViewport.removeEventListener("scroll", setRealVh);
      } else {
        window.removeEventListener("resize", setRealVh);
      }
    };
  }, [isIOS]);

  const editorRootRef = React.useRef<HTMLDivElement | null>(null);
  const [editorOffsetTop, setEditorOffsetTop] = useState(0);

  useEffect(() => {
    if (isMobile) return;

    const measure = () => {
      window.requestAnimationFrame(() => {
        const element = editorRootRef.current;
        if (!element) return;
        const top = element.getBoundingClientRect().top + window.scrollY;
        setEditorOffsetTop((prev) => (Math.abs(prev - top) > 1 ? top : prev));
      });
    };

    measure();

    const resizeObserver = new ResizeObserver(measure);
    resizeObserver.observe(document.body);
    window.addEventListener("resize", measure);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [isMobile]);

  const desktopEditorHeight = editorOffsetTop
    ? `calc(100dvh - ${editorOffsetTop}px)`
    : "85vh";

  const MOBILE_BOTTOM_OFFSET = 175;

  useEffect(() => {
    if (!isMobile) return;

    const iconsRow = document.getElementById("mobile-icons-row");
    const stripe = document.getElementById("mobile-stripe");

    if (!iconsRow || !stripe) return;

    const updateHeight = () => {
      window.requestAnimationFrame(() => {
        const totalHeight =
          iconsRow.offsetHeight + stripe.offsetHeight + MOBILE_BOTTOM_OFFSET;

        setBottomBarHeight(totalHeight);
      });
    };

    updateHeight();

    const resizeObserver = new ResizeObserver(updateHeight);

    resizeObserver.observe(iconsRow);
    resizeObserver.observe(stripe);

    window.addEventListener("resize", updateHeight);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener("resize", updateHeight);
    };
  }, [isMobile]);

  const rightPanelToggle = useMemo(
    () =>
      isMobile ? null : (
        <Tooltip title="Hide panel" placement="left" arrow>
          <IconButton
            size="small"
            disableRipple
            onClick={() => setIsRightPanelOpen(false)}
            aria-label="Hide panel"
            aria-expanded
            sx={{
              p: 0,
              flexShrink: 0,
              lineHeight: 0,
              width: editorTokens.rightRail.collapseSize,
              height: editorTokens.rightRail.collapseSize,
              borderRadius: editorTokens.rightRail.collapseRadius,
              color: editorTokens.palette.body,
              backgroundColor: "transparent",
              "&:hover": { backgroundColor: editorTokens.palette.surface },
            }}
          >
            <ChevronRightIcon size={editorTokens.rightRail.collapseIconSize} />
          </IconButton>
        </Tooltip>
      ),
    [isMobile],
  );

  const RightSidebarContent = useMemo(() => {
    return (
      <Box
        sx={{
          height: "100%",
          minHeight: 0,
          display: "flex",
          flexDirection: "row",
          alignItems: "stretch",
          backgroundColor: "background.paper",
        }}
      >
        <Box
          sx={{
            ...(isMobile
              ? { flex: 1, minWidth: 0 }
              : {
                  width: editorTokens.rightRail.width,
                  flexShrink: 0,
                  borderLeft: `1px solid ${editorTokens.rightRail.borderColor}`,
                }),
            minHeight: 0,
            display: isMobile || isRightPanelOpen ? "flex" : "none",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
              {error}
            </Alert>
          )}

          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
            }}
          >
            {activeRightPanel === "documents" && (
              <Documents
                docLoading={docLoading}
                headerAction={rightPanelToggle}
                variant={isMobile ? "signer" : "editor"}
                onAddDocuments={onAddDocuments}
              />
            )}
            {activeRightPanel === "chat" &&
              (isAIAvailable ? (
                <ChatWithPdf
                  signRequest={signRequest}
                  fillHeight={!isMobile}
                  headerAction={rightPanelToggle}
                  starterPrompts={EDITOR_AI_PROMPTS}
                />
              ) : (
                <GatedPanel
                  icon={
                    <AssistantTabIcon
                      size={editorTokens.rightRail.headerIconSize}
                    />
                  }
                  title="AI Assistant"
                  headerAction={rightPanelToggle}
                  aiAssistantState={aiAssistantState}
                />
              ))}
            {activeRightPanel === "insights" &&
              (isAIAvailable ? (
                <AIInsightsPanel
                  signRequest={signRequest}
                  headerAction={rightPanelToggle}
                />
              ) : (
                <GatedPanel
                  icon={
                    <InsightsTabIcon
                      size={editorTokens.rightRail.headerIconSize}
                    />
                  }
                  title="AI Insights"
                  headerAction={rightPanelToggle}
                  aiAssistantState={aiAssistantState}
                />
              ))}
            {activeRightPanel === "properties" && (
              <PropertiesDrawer
                onClose={() => setActiveRightPanel("documents")}
                headerAction={rightPanelToggle}
              />
            )}
          </Box>
        </Box>

        <Box
          component="nav"
          {...tourAttr("request.rightRail")}
          sx={{
            width: RIGHT_RAIL_WIDTH,
            flexShrink: 0,
            borderLeft: `1px solid ${editorTokens.tabRail.borderColor}`,
            backgroundColor: "background.paper",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            py: editorTokens.tabRail.paddingY,
            gap: editorTokens.tabRail.gap,
          }}
        >
          {visibleRightPanelIcons.map((panelIcon) => {
            const IconComponent = panelIcon.icon;
            const isActive =
              activeRightPanel === panelIcon.id && isRightPanelOpen;
            const isAIFeature =
              panelIcon.id === "chat" || panelIcon.id === "insights";
            const isLocked = isAIFeature && !isAIAvailable;
            const tooltipTitle = isLocked
              ? `${panelIcon.label} — ${aiDisabledTooltip}`
              : panelIcon.label;
            return (
              <Box
                key={panelIcon.id}
                sx={{
                  position: "relative",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Tooltip title={tooltipTitle} placement="left" arrow>
                  <IconButton
                    onClick={() => handleRightPanelChange(panelIcon.id)}
                    aria-label={panelIcon.label}
                    aria-pressed={isActive}
                    sx={{
                      width: editorTokens.tabRail.buttonSize,
                      height: editorTokens.tabRail.buttonSize,
                      borderRadius: editorTokens.tabRail.buttonRadius,
                      backgroundColor: isActive
                        ? editorTokens.tabRail.activeBg
                        : "transparent",
                      color: isActive
                        ? editorTokens.tabRail.activeColor
                        : editorTokens.tabRail.idleColor,
                      "&:hover": {
                        backgroundColor: isActive
                          ? editorTokens.tabRail.activeBg
                          : editorTokens.tabRail.hoverBg,
                      },
                      transition: "background-color 0.2s ease",
                      opacity: isLocked && !isActive ? 0.65 : 1,
                    }}
                  >
                    <IconComponent size={editorTokens.tabRail.iconSize} />
                  </IconButton>
                </Tooltip>
                {isLocked && (
                  <Box
                    sx={{
                      position: "absolute",
                      bottom: 2,
                      right: 2,
                      backgroundColor: "background.paper",
                      borderRadius: "50%",
                      width: editorTokens.tabRail.lockBadgeSize,
                      height: editorTokens.tabRail.lockBadgeSize,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      boxShadow: `0 0 0 1px ${editorTokens.rightRail.borderColor}`,
                      pointerEvents: "none",
                    }}
                  >
                    <LockOutlined
                      sx={{
                        fontSize: editorTokens.tabRail.lockIconSize,
                        color: editorTokens.palette.muted,
                      }}
                    />
                  </Box>
                )}
              </Box>
            );
          })}
        </Box>
      </Box>
    );
  }, [
    error,
    activeRightPanel,
    isMobile,
    handleDrawerClose,
    handleRightPanelChange,
    isAIAvailable,
    visibleRightPanelIcons,
    aiAssistantState,
    aiDisabledTooltip,
    signRequest,
    docLoading,
    theme.palette.primary.main,
    showAssignModal,
    isRightPanelOpen,
    rightPanelToggle,
    onAddDocuments,
  ]);

  // Counted the way Auto-place places them: spots already holding a field are
  // not offered again.
  const pendingAiFieldCount =
    isCreateFlow && selectedFile?.unsignedDoc
      ? withoutOverlappingFields(
          pendingAiFields[selectedFile.unsignedDoc] ?? [],
          selectedFile?.fields || [],
          (selectedFile as any)?.dimensions,
        ).length
      : 0;

  const isCompactBanner = !isMobile && isNarrowViewport;

  const { aiBanner } = editorTokens;

  const aiFieldsBadge =
    pendingAiFieldCount > 0 ? (
      <Box
        sx={{
          maxWidth: "100%",
          backgroundColor: aiBanner.bg,
          color: aiBanner.color,
          borderRadius: aiBanner.radius,
          padding: isMobile
            ? { xs: "6px 8px", sm: aiBanner.padding }
            : isCompactBanner
              ? "6px 6px 6px 12px"
              : aiBanner.padding,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexWrap: isMobile ? "wrap" : "nowrap",
          columnGap: isMobile ? { xs: 0.5, sm: 1 } : aiBanner.gap,
          rowGap: 0.5,
          boxShadow: aiBanner.shadow,
          // The design's pill carries no dismiss control. Keep the capability,
          // but float it outside the pill and reveal it on hover so the pill's
          // resting geometry is exactly the design's.
          position: "relative",
          "& [data-ai-dismiss]": {
            opacity: isMobile ? 1 : 0,
            pointerEvents: isMobile ? "auto" : "none",
            transition: "opacity .15s ease",
          },
          "&:hover [data-ai-dismiss]": { opacity: 1, pointerEvents: "auto" },
        }}
      >
        <Box
          component="span"
          sx={{ display: "flex", color: aiBanner.iconColor, flexShrink: 0 }}
        >
          <SparkleIcon
            size={isMobile ? 14 : isCompactBanner ? 14 : aiBanner.iconSize}
          />
        </Box>
        <Typography
          component="span"
          sx={{
            fontWeight: aiBanner.fontWeight,
            fontSize: isMobile ? "0.72rem" : aiBanner.fontSize,
            color: aiBanner.color,
            whiteSpace: isMobile ? "normal" : "nowrap",
            overflowWrap: "anywhere",
            textAlign: "center",
            minWidth: 0,
            lineHeight: "normal",
          }}
        >
          {isCompactBanner ? (
            <>
              AI found{" "}
              <Box component="span" sx={{ fontWeight: 700 }}>
                {pendingAiFieldCount}
              </Box>{" "}
              spots
            </>
          ) : (
            <>
              Inkfree AI found{" "}
              <Box component="span" sx={{ fontWeight: 700 }}>
                {pendingAiFieldCount}
              </Box>{" "}
              likely signature spots
            </>
          )}
        </Typography>
        <Button
          onClick={handleAutoPlaceFields}
          sx={{
            "&.MuiButton-root": {
              borderRadius: aiBanner.actionRadius,
              textTransform: "none",
              background: aiBanner.actionBg,
              color: aiBanner.color,
              border: "none",
              "&:hover": { background: aiBanner.actionHoverBg },
              padding: aiBanner.actionPadding,
              fontSize: aiBanner.actionFontSize,
              fontWeight: aiBanner.actionFontWeight,
              letterSpacing: "normal",
              lineHeight: "normal",
              minWidth: "auto",
              boxShadow: "none",
              flexShrink: 0,
            },
          }}
        >
          Auto-place
        </Button>
        <IconButton
          size="small"
          data-ai-dismiss
          aria-label="Dismiss detected fields"
          onClick={() => {
            if (selectedFile?.unsignedDoc) {
              dispatch(
                clearPendingAiFields({ docId: selectedFile.unsignedDoc }),
              );
            }
          }}
          sx={{
            color: aiBanner.color,
            p: 0.4,
            flexShrink: 0,
            ...(isMobile
              ? {}
              : {
                  position: "absolute",
                  top: -6,
                  right: -6,
                  backgroundColor: aiBanner.bg,
                  boxShadow: aiBanner.shadow,
                  "&:hover": { backgroundColor: aiBanner.bg },
                }),
          }}
        >
          <EditorCloseIcon size={aiBanner.closeIconSize} />
        </IconButton>
      </Box>
    ) : null;

  return (
    <>
      <Grid
        container
        ref={editorRootRef}
        spacing={isMobile ? 2 : 0}
        sx={{
          height: isMobile ? { xs: "auto", sm: "auto" } : desktopEditorHeight,
          backgroundColor: "background.paper",
          overflow: isMobile ? { xs: "visible", sm: "visible" } : "hidden",
          ...(!isMobile && {
            flexWrap: "nowrap",
            alignItems: "stretch",
          }),
          ...(!isMobile &&
            isFullScreen && {
              position: "fixed",
              inset: 0,
              zIndex: (theme: any) => theme.zIndex.modal,
              width: "100%",
              height: "100vh",
              margin: 0,
            }),
        }}
      >
        {isMobile && (
          <Grid
            item
            sx={{
              bgcolor: "background.paper",
              borderColor: "common.white",
              height: { xs: "auto", sm: "auto", md: "calc(100vh - 64px)" },
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              width: "100%",
              flexDirection: "column",
              gap: 1.5,
            }}
          >
            <Box
              sx={{
                width: "fit-content",
                maxWidth: "100%",
                boxSizing: "border-box",
                px: "14px",
              }}
            >
              <DocumentTabMobile
                signRequest={signRequest}
                docLoading={docLoading}
                isMobile={isMobile}
              />
            </Box>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                px: "14px",
                width: "90%",
              }}
            >
              {isSelfSign ? (
                <SelfSignerInfo
                  userName={loggedInUser?.userName}
                  email={loggedInUser?.email}
                  isMobile={isMobile}
                />
              ) : (
                participantsDropDown(
                  participants,
                  selectedParticipant,
                  dispatch,
                  anchorEl,
                  handleOpen,
                  handleClose,
                  isBulkSend,
                  isMobile,
                )
              )}
            </Box>
          </Grid>
        )}
        {isLeftPanelVisible && (
          <Grid
            item
            {...(isMobile ? gridSizes.leftPanel : {})}
            id={isMobile ? "mobile-icons-row" : undefined}
            component={isMobile ? "div" : "aside"}
            sx={{
              bgcolor: "background.paper",
              height: isMobile ? { xs: "auto", sm: "auto" } : "100%",
              order: isMobile ? { xs: 2, sm: 2 } : 1,
              ...(isMobile
                ? {
                    borderColor: "common.white",
                    overflowY: "auto",
                    "&.MuiGrid-item": { paddingTop: "8px" },
                  }
                : {
                    width: editorTokens.leftRail.width,
                    flex: `0 0 ${editorTokens.leftRail.width}px`,
                    maxWidth: editorTokens.leftRail.width,
                    minWidth: editorTokens.leftRail.width,
                    borderRight: `1px solid ${editorTokens.leftRail.borderColor}`,
                    display: "flex",
                    flexDirection: "column",
                    minHeight: 0,
                    overflow: "hidden",
                    "&.MuiGrid-item": { padding: 0 },
                  }),
            }}
          >
            {isMobile ? (
              <Box p={1}>
                {rolesRail}
                <FieldsPanelMobile
                  setIsFieldWithinBounds={setIsFieldWithinBounds}
                  setPreviewCoords={setPreviewCoords}
                  selectedField={selectedField}
                  setSelectedField={setSelectedField}
                  isMobile={isMobile}
                />
              </Box>
            ) : (
              <>
                <Box
                  {...tourAttr("request.participantRail")}
                  sx={{
                    flexShrink: 0,
                    p: rolesRail ? 0 : editorTokens.leftRail.headerPadding,
                    borderBottom: `1px solid ${editorTokens.leftRail.borderColor}`,
                  }}
                >
                  {rolesRail ? (
                    rolesRail
                  ) : isSelfSign ? (
                    <SelfSignerInfo
                      userName={loggedInUser?.userName}
                      email={loggedInUser?.email}
                    />
                  ) : (
                    <>
                      <Typography sx={railEyebrowSx}>
                        Placing fields for
                      </Typography>
                      {participantsDropDown(
                        participants,
                        selectedParticipant,
                        dispatch,
                        anchorEl,
                        handleOpen,
                        handleClose,
                        isBulkSend,
                        false,
                        true,
                      )}
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: editorTokens.leftRail.swatchRowGap,
                          mt: editorTokens.leftRail.swatchRowMarginTop,
                        }}
                      >
                        {participants.map((participant: RequestParticipant) => {
                          const swatch =
                            participant.color || editorTokens.palette.accent;
                          return (
                            <Tooltip
                              key={participant.id}
                              describeChild
                              title={
                                participant.name ||
                                participant.requestRole ||
                                participant.email ||
                                ""
                              }
                              arrow
                            >
                              <Box
                                sx={{
                                  width: editorTokens.leftRail.swatchSize,
                                  height: editorTokens.leftRail.swatchSize,
                                  flexShrink: 0,
                                  borderRadius: "50%",
                                  backgroundColor: swatch,
                                  border: `${editorTokens.leftRail.swatchRingWidth} solid ${alpha(
                                    swatch,
                                    0.25,
                                  )}`,
                                }}
                              />
                            </Tooltip>
                          );
                        })}
                        {onManageParticipants && (
                          <Button
                            onClick={() => onManageParticipants()}
                            startIcon={
                              <PlusIcon
                                size={editorTokens.leftRail.manageIconSize}
                              />
                            }
                            sx={{
                              "&.MuiButton-root": {
                                ml: "auto",
                                minWidth: "auto",
                                p: editorTokens.leftRail.managePadding,
                                borderRadius:
                                  editorTokens.leftRail.manageRadius,
                                color: editorTokens.palette.accent,
                                fontSize: editorTokens.leftRail.manageFontSize,
                                fontWeight:
                                  editorTokens.leftRail.manageFontWeight,
                                textTransform: "none",
                                letterSpacing: "normal",
                                lineHeight: "normal",
                                "& .MuiButton-startIcon": {
                                  mr: editorTokens.leftRail.manageGap,
                                  ml: 0,
                                },
                                "&:hover": {
                                  background: editorTokens.palette.surface,
                                },
                              },
                            }}
                          >
                            Manage
                          </Button>
                        )}
                      </Box>
                    </>
                  )}
                </Box>
                <FieldsPanel
                  setIsFieldWithinBounds={setIsFieldWithinBounds}
                  setPreviewCoords={setPreviewCoords}
                  selectedField={selectedField}
                  setSelectedField={setSelectedField}
                />
              </>
            )}
          </Grid>
        )}

        <Grid
          item
          {...(isMobile ? mainPanelSizes : {})}
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: isMobile ? "center" : "stretch",
            // height: "100%",
            padding: isMobile ? "1px 14px !important" : "0 !important",
            backgroundColor: "background.paper",
            overflow: "hidden",
            height: isMobile
              ? {
                  xs: isIOS
                    ? `calc(var(--real-vh, 1vh) * 100 - ${bottomBarHeight}px)`
                    : `calc(100dvh - ${bottomBarHeight}px)`,
                  sm: isIOS
                    ? `calc(var(--real-vh, 1vh) * 100 - ${bottomBarHeight}px)`
                    : `calc(100dvh - ${bottomBarHeight}px)`,
                }
              : "100%",
            order: isMobile ? { xs: 1, sm: 1 } : 2,
            borderLeft: isMobile
              ? `1px solid ${theme.palette.neutral[300]}`
              : "none",
            ...(isMobile && { ml: "14px" }),
            "&.MuiGrid-item": {
              paddingTop: isMobile ? 2 : 0,
            },
            ...(!isMobile && {
              flexGrow: 1,
              flexBasis: 0,
              maxWidth: "none",
              minWidth: 0,
            }),
          }}
        >
          <Box
            component={isMobile ? "div" : "section"}
            sx={{
              flex: 1,
              overflow: "hidden",
              width: "100%",
              height: "100%",
              display: "flex",
              flexDirection: "column",
              position: "relative",
              ...(isMobile
                ? {
                    backgroundColor: "background.paper",
                    maxWidth: "1300px",
                    border: `1px solid ${theme.palette.neutral[200]}`,
                    boxShadow: "0 8px 24px rgba(0, 0, 0, 0.06)",
                    borderRadius: 1,
                  }
                : {
                    minWidth: 0,
                    backgroundColor: editorTokens.canvas.bg,
                  }),
            }}
          >
            <Stack
              direction="row"
              justifyContent={isMobile ? "flex-end" : "flex-start"}
              alignItems="center"
              spacing={isMobile ? 1 : 0}
              sx={{
                flexShrink: 0,
                backgroundColor: "background.paper",
                borderBottom: `1px solid ${editorTokens.canvas.toolbarBorder}`,
                position: "relative",
                ...(isMobile
                  ? { px: 1.5, py: 1 }
                  : {
                      height: editorTokens.canvas.toolbarHeight,
                      px: editorTokens.canvas.toolbarPaddingX,
                      gap: editorTokens.canvas.toolbarGap,
                    }),
              }}
            >
              {!isMobile && (
                <>
                  <Tooltip
                    title={
                      isLeftPanelOpen
                        ? "Hide fields panel"
                        : "Show fields panel"
                    }
                    arrow
                  >
                    <IconButton
                      onClick={() => setIsLeftPanelOpen((prev) => !prev)}
                      aria-label={
                        isLeftPanelOpen
                          ? "Hide fields panel"
                          : "Show fields panel"
                      }
                      aria-pressed={isLeftPanelOpen}
                      sx={canvasIconButtonSx(
                        editorTokens.canvas.toggleSize,
                        editorTokens.canvas.toggleRadius,
                      )}
                    >
                      <PanelToggleIcon
                        size={editorTokens.canvas.toggleIconSize}
                      />
                    </IconButton>
                  </Tooltip>
                  {selectedFileName && (
                    <Typography
                      component="h1"
                      sx={{
                        fontSize: editorTokens.canvas.fileNameFontSize,
                        fontWeight: editorTokens.canvas.fileNameFontWeight,
                        color: editorTokens.palette.ink,
                        lineHeight: "normal",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        minWidth: 0,
                      }}
                    >
                      {selectedFileName}
                    </Typography>
                  )}
                  {pageInfo.total > 0 && (
                    <Typography
                      sx={{
                        fontSize: editorTokens.canvas.pageMetaFontSize,
                        color: editorTokens.canvas.pageMetaColor,
                        lineHeight: "normal",
                        whiteSpace: "nowrap",
                        flexShrink: 0,
                      }}
                    >
                      Page {pageInfo.current} of {pageInfo.total}
                    </Typography>
                  )}
                  <Box sx={{ flex: 1 }} />
                  <Tooltip
                    title={isFullScreen ? "Exit full screen" : "Full screen"}
                    arrow
                  >
                    <IconButton
                      onClick={() => setIsFullScreen((prev) => !prev)}
                      aria-label={
                        isFullScreen ? "Exit full screen" : "Full screen"
                      }
                      aria-pressed={isFullScreen}
                      sx={canvasIconButtonSx(
                        editorTokens.canvas.actionSize,
                        editorTokens.canvas.actionRadius,
                      )}
                    >
                      <FitScreenIcon
                        size={editorTokens.canvas.actionIconSize}
                      />
                    </IconButton>
                  </Tooltip>
                  <Box
                    sx={{
                      width: "1px",
                      height: editorTokens.canvas.dividerHeight,
                      backgroundColor: editorTokens.canvas.toolbarBorder,
                      flexShrink: 0,
                    }}
                  />
                </>
              )}
              <UndoRedoButtons />
            </Stack>
            <Box
              sx={{
                flex: 1,
                minHeight: 0,
                display: "flex",
                position: "relative",
              }}
            >
              <DocumentViewer
                docLoading={docLoading}
                setDocLoading={setDocLoading}
                isFieldWithinBounds={isFieldWithinBounds}
                previewCoords={previewCoords}
                selectedPreviewField={selectedField}
                setSelectedPreviewField={setSelectedField}
                isMobile={isMobile}
                onFieldSelect={() => setActiveRightPanel("properties")}
                onPasteAnchor={setPasteAnchor}
                pasteFieldAt={pasteFieldAt}
                canPaste={!!clipboardField}
                zoom={1}
                topBanner={aiFieldsBadge}
                onPageInfoChange={handlePageInfoChange}
                goToPageRef={goToPageRef}
              />
              {!isMobile && pageInfo.total > 1 && (
                <Box
                  sx={{
                    position: "absolute",
                    bottom: editorTokens.pageNav.bottom,
                    left: "50%",
                    transform: "translateX(-50%)",
                    display: "flex",
                    alignItems: "center",
                    gap: editorTokens.pageNav.gap,
                    p: editorTokens.pageNav.padding,
                    backgroundColor: editorTokens.pageNav.bg,
                    border: `1px solid ${editorTokens.pageNav.border}`,
                    borderRadius: editorTokens.pageNav.radius,
                    boxShadow: editorTokens.pageNav.shadow,
                    zIndex: 5,
                  }}
                >
                  <IconButton
                    aria-label="Previous page"
                    disabled={pageInfo.current <= 1}
                    onClick={() => goToPageRef.current?.(pageInfo.current - 1)}
                    sx={pageNavButtonSx}
                  >
                    <ChevronLeftIcon size={editorTokens.pageNav.iconSize} />
                  </IconButton>
                  <Typography
                    sx={{
                      px: editorTokens.pageNav.labelPaddingX,
                      fontSize: editorTokens.pageNav.labelFontSize,
                      fontWeight: editorTokens.pageNav.labelFontWeight,
                      color: editorTokens.palette.ink,
                      lineHeight: "normal",
                      userSelect: "none",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {pageInfo.current} / {pageInfo.total}
                  </Typography>
                  <IconButton
                    aria-label="Next page"
                    disabled={pageInfo.current >= pageInfo.total}
                    onClick={() => goToPageRef.current?.(pageInfo.current + 1)}
                    sx={pageNavButtonSx}
                  >
                    <ChevronRightIcon size={editorTokens.pageNav.iconSize} />
                  </IconButton>
                </Box>
              )}
            </Box>
          </Box>
        </Grid>

        {!isMobile &&
          (() => {
            // Panel + icon strip are one flex column pair; the strip is always
            // visible, the panel collapses out of the width when hidden.
            const railWidth = isRightPanelOpen
              ? editorTokens.rightRail.width + RIGHT_RAIL_WIDTH
              : RIGHT_RAIL_WIDTH;
            return (
              <Grid
                item
                sx={{
                  bgcolor: "background.paper",
                  overflow: "hidden",
                  height: "100%",
                  minHeight: 0,
                  order: 3,
                  mt: 0,
                  display: "flex",
                  flexDirection: "column",
                  position: "relative",
                  flex: `0 0 ${railWidth}px`,
                  width: railWidth,
                  minWidth: railWidth,
                  maxWidth: railWidth,
                  "&.MuiGrid-item": { padding: 0 },
                }}
              >
                {RightSidebarContent}
              </Grid>
            );
          })()}
      </Grid>

      {/* {isMobile && (
        <Fab
          color="primary"
          aria-label="Open settings"
          sx={{
            position: "fixed",
            bottom: 16,
            right: 16,
            zIndex: 1000,
          }}
          onClick={handleDrawerToggle}
        >
          <Settings />
        </Fab>
      )} */}

      <Drawer
        anchor="bottom"
        open={isRightDrawerOpen}
        onClose={handleDrawerClose}
        sx={{
          "& .MuiDrawer-paper": {
            width: "100%",
            maxWidth: 400,
            bgcolor: "background.paper",
            display: "flex",
            flexDirection: "column",
          },
        }}
        ModalProps={{
          keepMounted: true,
        }}
      >
        {RightSidebarContent}
      </Drawer>

      {showAssignModal && (
        <AssignExistingFieldsModal
          isDialogOpen={showAssignModal}
          handleDialogClose={() => setShowAssignModal(false)}
          files={signRequest.files}
          signers={participants}
          assignExistingFields={assignExistingFields}
        />
      )}

      <ContractAtAGlanceDialog
        open={isContractDialogOpen}
        onClose={() => setIsContractDialogOpen(false)}
        envelopeId={(signRequest as any).id}
        selectedFile={selectedFile}
      />

      <AppModal
        open={isConfirmAutoPlaceOpen}
        onClose={() => setIsConfirmAutoPlaceOpen(false)}
        title="Confirm Auto-place"
        tone="neutral"
        size="sm"
        primaryLabel="Add Fields"
        onPrimary={() => {
          setIsConfirmAutoPlaceOpen(false);
          handleAutoPlaceFields(true);
        }}
        cancelLabel="Cancel"
        onCancel={() => setIsConfirmAutoPlaceOpen(false)}
      >
        <Typography sx={bodyTextSx}>
          Existing fields will remain. Detected fields will be added on top.
          Continue?
        </Typography>
      </AppModal>
    </>
  );
};

export default SignRequestEditor;
